<template>
  <div id="page-details" class="mh-5">
    <slot v-bind:model="model"> </slot>

    <vs-alert
      color="danger"
      :title="entity + ' Not Found'"
      :active.sync="not_found"
    >
      <span>Record with id: {{ $route.params.id }} not found. </span>
      <span>
        <span>Check </span
        ><router-link :to="listRoute" class="text-inherit underline"
          >All {{ entity }}s</router-link
        >
      </span>
    </vs-alert>

    <vue-element-loading v-if="showLoading" :active="loading"/>

    <div class="vx-row" v-if="model && model.id">
      <div class="vx-col sm:w-1/2">
        <div class="mt-8 flex flex-wrap items-center justify-start">
          <vs-button @click="goToList" class="mt-2"> Back </vs-button>
          <vs-button
            v-if="
              !isDeleted &&
              componentAccess.canDelete &&
              componentAccess.canDelete.access
            "
            @click="confirmDelete"
            class="ml-4 mt-2"
            :disabled="loading"
          >
            Delete
          </vs-button>
          <slot name="extra-button" v-if="!isDeleted" v-bind:model="model"></slot>
        </div>
      </div>
      <div class="vx-col sm:w-1/2">
        <div class="mt-8 flex flex-wrap items-center justify-end">
          <vs-button
            v-if="
              !isDeleted &&
              componentAccess.canEdit &&
              componentAccess.canEdit.access
            "
            @click="goToEdit"
            class="mt-2"
            color="light"
            :disabled="loading"
          >
            Edit
          </vs-button>
          <vs-button
            v-if="
              isDeleted &&
              componentAccess.canRestore &&
              componentAccess.canRestore.access
            "
            @click="confirmRestore"
            class="ml-4 mt-2"
            color="light"
            :disabled="loading"
          >
            Restore
          </vs-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ViewModelMixin from '@/components/mixins/ViewModelMixin';
import CanAccessMixin from '@/components/mixins/CanAccessMixin';

export default {
  name: 'ViewPage',
  props: {
    baseRoute: {
      type: String,
      required: true
    },
    entity: {
      type: String,
      required: true
    },
    modelEntity: {
      required: false
    },
    pageTitle: {
      type: [String, Function, Promise],
      required: false,
    },
    showLoading: {
      type: Boolean,
      default: true
    },
    buttonAccess: {
      type: Object,
      default: {
        canEdit: {
          route: '',
          access: false
        },
        canDelete: {
          route: '',
          access: false
        },
        canRestore: {
          route: '',
          access: false
        }
      }
    },
    disableButtons: {
      type: Boolean,
      default: false
    },
    skipAutoFetch: {
      type: Boolean,
      default: false
    },
    externalModel: {
      type: Object,
      required: false,
      default: null
    }
  },
  mixins: [ViewModelMixin, CanAccessMixin],
  watch: {
    externalModel: {
      handler(newModel) {
        // Only update model if externalModel is provided (not null/undefined)
        // When skipAutoFetch is false, ViewModelMixin will handle fetching
        if (newModel && Object.keys(newModel).length > 0) {
          this.model = newModel;
        }
      },
      immediate: true
    },
    modelEntity: {
      handler(newEntity) {
        this.apiEntity = newEntity;
      },
      immediate: true
    }
  },
  async mounted() {
    // If skipAutoFetch is true, parent component will handle fetching
    // Otherwise, use the mixin's performAutoFetch method
    if (!this.skipAutoFetch) {
      await this.performAutoFetch();
    } else {
      // If externalModel is provided, use it; otherwise model stays as {}
      if (this.externalModel) {
        this.model = this.externalModel;
      }
      // Still handle pageTitle if provided, even when skipping fetch
      if (this.pageTitle) {
        let pageTitle = this.pageTitle;
        if (typeof this.pageTitle === 'function') {
          pageTitle = this.pageTitle(this.model);
        }
        await this.$store.dispatch('updatePageTitle', pageTitle);
      }
    }
  },
  computed: {
    listRoute() {
      return { name: this.baseRoute + '.list' };
    }
  },
  methods: {
    goToList() {
      this.$router.push(this.listRoute);
    },
    goToEdit() {
      this.$router.push({ name: this.baseRoute + '.edit' });
    },
    async deleteAction() {
      try {
        await this.apiEntity.delete(this.id);
        this.$vs.notify({
          color: 'success',
          title: this.entity + ' Deleted',
          text: this.entity + ' was successfully deleted',
          position: 'top-right'
        });
        this.goToList();
      } catch (e) {
        console.log(e);
        this.$vs.notify({
          color: 'danger',
          title: this.entity + ' failed to delete',
          position: 'top-right'
        });
      }
    },
    async restoreAction() {
      try {
        await this.apiEntity.restore(this.id);
        this.$vs.notify({
          color: 'success',
          title: this.entity + ' Restored',
          text: this.entity + ' was successfully restored',
          position: 'top-right'
        });
        this.$emit('restore');
        this.goToList();
      } catch (e) {
        console.log(e);
        this.$vs.notify({
          color: 'danger',
          title: this.entity + ' failed to restore',
          position: 'top-right'
        });
      }
    },
    confirmRestore() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Confirm Restore',
        text: `You are about to restore this ${this.entity}`,
        accept: this.restoreAction,
        acceptText: 'Restore'
      });
    },

    confirmDelete() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: `You are about to delete this ${this.entity}`,
        accept: this.deleteAction,
        acceptText: 'Delete'
      });
    },
    showDeleteSuccess() {
      this.$vs.notify({
        color: 'success',
        title: this.entity + ' Deleted',
        text: `The selected ${this.entity} was successfully deleted`
      });
    }
  }
};
</script>

<style scoped>
#page-details {
  min-height: 200px;
}
</style>
